import React, { useCallback } from 'react';
import { EOrganizationGroupAccessRequestStatus, EOrganizationGroupClickType, EOrganizationGroupVisibility, OrganizationGroup }
 from '../../types/objects';
import Profile from '@common/components/profile';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { Organisation } from '@common/types/objects';
import ConfirmButton from '@common/components/confirm-button';
import { Link, useParams } from 'react-router-dom';

const validVisibilities = [
  EOrganizationGroupVisibility.PUBLIC,
  EOrganizationGroupVisibility.PUBLIC_WITH_APPROVAL
];

interface Props {
  organizationGroup: OrganizationGroup;
  onSelectChannel: (channel: string) => void;
  selectedOrganisation: Organisation;
  handleClickGroup: (group: OrganizationGroup, type: EOrganizationGroupClickType) => void;
  loading: boolean;
  isClickable: boolean;
}

const OrganizationGroupCard = (
  {
 organizationGroup, onSelectChannel, selectedOrganisation, handleClickGroup, loading, isClickable
}: Props) => {
  const { networkId } = useParams<{ networkId: string }>();
  const { t } = useTranslation();
  const handleSelectChannel = useCallback(() => {
    onSelectChannel(organizationGroup.id);
  }, [organizationGroup.id, onSelectChannel]);

  const profileComponent = (
    <Profile
      size={40}
      user={{
        full_name: organizationGroup.name,
        profile_img: organizationGroup.theme.logo_url || selectedOrganisation.brand_icon,
        nameClassName: 'tw-line-clamp-1'
      }}
    >
      <p className="tw-text-gray-custom-430 tw-m-0 tw-pt-1">
        {organizationGroup.member_count > 1 ?
          t('organisation:organization_group_members', { count: organizationGroup.member_count })
          : t('organisation:organization_group_member', { count: organizationGroup.member_count })}
      </p>
    </Profile>
  );

  const renderConfirmButton = (
    titleKey: string,
    descriptionKey: string,
    confirmTextKey: string,
    buttonTextKey: string,
    actionType: EOrganizationGroupClickType,
    isDanger: boolean = false
  ) => (
    <ConfirmButton
      title={t(titleKey)}
      description={(
        <Trans
          i18nKey={descriptionKey}
          values={{ name: organizationGroup.name }}
          components={[<b />]}
        />
      )}
      confirmText={t(confirmTextKey)}
      onConfirm={() => handleClickGroup(organizationGroup, actionType)}
    >
      <Button
        type="inverted"
        className={isDanger ? 'tw-text-red-link hover:!tw-text-red-link hover:!tw-bg-red-light' : ''}
        size="small"
        isLoading={loading}
      >
        {t(buttonTextKey)}
      </Button>
    </ConfirmButton>
  );

  return (
    <div
      className={`tw-flex tw-items-center tw-justify-between tw-p-5
    tw-border-b tw-border-x-0 tw-border-t-0 tw-border-gray-100 tw-border-solid tw-bg-white tw-rounded-lg`}
    >
      {isClickable ? (
        <Link
          onClick={handleSelectChannel}
          className="tw-cursor-pointer"
          to={`/networks/${networkId}/messages/channel/${organizationGroup.id}`}
        >
          {profileComponent}
        </Link>
      ) : profileComponent}

      {organizationGroup.is_member && validVisibilities.includes(organizationGroup.visibility) ? (
        renderConfirmButton(
          'organisation:leave_group_modal_title',
          'organisation:leave_group_modal_description',
          'organisation:leave_group',
          'organisation:leave_group',
          EOrganizationGroupClickType.LEAVE,
          true
        )
      ) : organizationGroup.access_request_status === EOrganizationGroupAccessRequestStatus.PENDING ? (
        renderConfirmButton(
          'organisation:revoke_access_modal_title',
          'organisation:revoke_access_modal_description',
          'organisation:revoke_access',
          'organisation:revoke_access',
          EOrganizationGroupClickType.REVOKE,
          true
        )
      ) : !organizationGroup.is_member && validVisibilities.includes(organizationGroup.visibility) ?
      organizationGroup.visibility === EOrganizationGroupVisibility.PUBLIC ? (
        <Button
          type="inverted" size="small" isLoading={loading}
          onClick={() => handleClickGroup(organizationGroup, EOrganizationGroupClickType.JOIN)}
        >
          {t('organisation:join_group')}
        </Button>
      ) : (
        renderConfirmButton(
          'organisation:join_group_modal_title',
          'organisation:join_group_modal_description',
          'organisation:join_group',
          'organisation:join_group',
          EOrganizationGroupClickType.JOIN,
          false
        )
      ) : null}
    </div>);
};

export default OrganizationGroupCard;
